import { motion } from 'framer-motion'
import type { CSSProperties } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/utils/cn'

type Props = {
  title: string
  count: number
  color?: string
  icon?: LucideIcon
  delay?: number
  refreshing?: boolean
  onClick?: () => void
  className?: string
}

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6) return `rgba(128, 128, 128, ${alpha})`
  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const MARKER_BOX = 'h-9 w-9 shrink-0'

export function KpiCard({
  title,
  count,
  color = '#DC2925',
  icon: Icon,
  delay = 0,
  refreshing = false,
  onClick,
  className,
}: Props) {
  const accentVars = {
    '--kpi-accent': color,
    '--kpi-accent-muted': hexToRgba(color, 0.32),
  } as CSSProperties

  const surfaceStyle = {
    ...accentVars,
    background: `linear-gradient(165deg, ${hexToRgba(color, 0.08)} 0%, ${hexToRgba(color, 0.02)} 40%, rgb(var(--color-card)) 78%)`,
  } as const

  const inner = (
    <div className="kpi-card-surface shadow-kpi" style={surfaceStyle}>
      <div className="kpi-card-layout">
        <div className="kpi-card-header">
          {Icon ? (
            <div
              className={cn(
                MARKER_BOX,
                'flex items-center justify-center rounded-xl text-white',
                'shadow-[0_2px_8px_rgba(0,0,0,0.12)] ring-1 ring-white/20',
              )}
              style={{ background: color }}
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
            </div>
          ) : (
            <div className={cn(MARKER_BOX, 'flex items-center justify-center')} aria-hidden>
              <span
                className="h-2.5 w-2.5 rounded-full shadow-sm ring-2 ring-background/90"
                style={{ background: color }}
              />
            </div>
          )}

          <p className="kpi-card-title" title={title}>
            {title}
          </p>
        </div>

        <div className="kpi-card-metric">
          <motion.span
            key={count}
            initial={{ opacity: 0.6, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
            className={cn('kpi-card-count tabular-nums', refreshing && 'kpi-card-count--refreshing')}
            style={{ color }}
          >
            {count.toLocaleString()}
          </motion.span>
        </div>
      </div>
    </div>
  )

  const rootClass = cn(
    'kpi-card-root relative block h-full w-full overflow-visible text-left',
    refreshing && 'kpi-card-root--refreshing',
    onClick &&
      'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    className,
  )

  if (onClick) {
    return (
      <motion.button
        type="button"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay, ease: [0.22, 1, 0.36, 1] }}
        onClick={onClick}
        className={rootClass}
        style={accentVars}
      >
        {inner}
      </motion.button>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay, ease: [0.22, 1, 0.36, 1] }}
      className={rootClass}
      style={accentVars}
    >
      {inner}
    </motion.div>
  )
}

export const KPI_TONE_COLORS: Record<string, string> = {
  default: '#1A1A2E',
  blue: '#5794F2',
  emerald: '#06D001',
  amber: '#FF7F11',
  purple: '#8B5CF6',
  red: '#DC2925',
}
